import clsx from 'clsx'
import { defineComponent, reactive, Teleport, watch, computed, Transition, nextTick, type Ref, useId } from 'vue'

import '@/styles/animations/scale.css'

import useMenuModal from '@/composables/common/modal/useMenuModal'
import useMenu from '@/composables/common/useMenu'
import { type MenuAttach } from '@/utils/element'

import MenuModal from './modal/MenuModal'
import TeleportContent from './teleport/TeleportContent'

export interface VMenuActivatorProps {
  ref: Ref<HTMLElement | null>
  isFocus: boolean
  onClick?: (e: Event) => void
}

export interface MenuItemProps {
  onClick?: (e: Event) => void
}

interface Props {
  class?: string
  tData?: string
  menuContentId?: string,
  menuContentClass?: string,
  attach?: MenuAttach
  scrollIntoViewEnabled?: boolean
  disabled?: boolean
  isMobileMode?: boolean
  onInput?: (value: any) => void
  onFocus?: () => void
  label?: string
}

export default defineComponent<Props>((props: Props, context) => {
  const menuModalId = useId()
  const tData = computed((): string => props.tData ?? 'v-menu')
  const attach = computed((): MenuAttach => props.attach ?? 'body')
  const menuModal = useMenuModal(Symbol(`${menuModalId}-menu-menu-modal`))

  const {
    activatorRootEml,
    isFocus,
    menuContent,
    menuContentStyle
  } = useMenu(reactive({ attach, disabled: computed(() => !!props.disabled || !!props.isMobileMode) }))

  // #region Watchers

  watch(() => isFocus.value, () => {
    if (!isFocus.value) {
      activatorRootEml.value?.blur()
    } else {
      props.onFocus?.()
      if (props.scrollIntoViewEnabled) {
        nextTick(() => {
          const menuItemElm = document.getElementById(`${props.menuContentId}`)

          if (menuItemElm === null) {
            return
          }

          menuItemElm.scrollIntoView?.()
          menuContent.value?.scrollBy(0, -72)
        })
      }
    }
  })

  // #endregion

  return () => (
    <div
      t-data={tData.value}
      class={['relative', props.class]}
    >
      {context.slots.activator?.({
        ref: activatorRootEml,
        isFocus: isFocus.value,
        onClick: (e) => {
          if (!props.isMobileMode) {
            e.preventDefault()
          } else {
            menuModal.reveal()
          }
        }
      } satisfies VMenuActivatorProps)}
      <Teleport to='body'>
        <Transition name='v-scale'>
          {isFocus.value &&
            <TeleportContent
              t-data={`${tData.value}-content`}
              mainRef={menuContent}
              id={props.menuContentId}
              class={clsx([
                'absolute z-9999 flex flex-col rounded border border-neutral-100 bg-white py-2 shadow-elevation-1',
                props.menuContentClass
              ])}
              style={menuContentStyle.value}
            >
              {context.slots.default?.()}
            </TeleportContent>}
        </Transition>
      </Teleport>
      {menuModal.isRevealed.value &&
        <MenuModal
          t-data={`${tData.value}-menu-modal`}
          modalkey={menuModal.key}
          class=''
          title={props.label}
        >
          {context.slots.default?.({
            onClick: () => {
              menuModal.cancel()
            }
          } satisfies MenuItemProps)}
        </MenuModal>}
    </div>
  )
}, {
  name: 'VMenu',
  props: [
    'class',
    'tData',
    'menuContentId',
    'menuContentClass',
    'attach',
    'scrollIntoViewEnabled',
    'disabled',
    'isMobileMode',
    'onInput',
    'onFocus',
    'label'
  ]
})
